import React, { useEffect, useState } from 'react';
import './AssignmentAdd.scss';
import { assessmentList, runAdd, userList, departmentList, programmeList } from '../../../shared/settings/apiConfig';
import { postApi } from '../../../shared/services/apiHelper';
import { Back } from '../../../shared/icons/Icons';
import moment from 'moment';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DropdownInputMultiple, DropdownInput, TextInput } from '../../../shared/form/FormInput';
import { useSelector } from 'react-redux';

const AssignmentAdd = (props) => {
    const loggedInUserData = useSelector((state) => state.user);

    const [assignmentName, setAssignmentName] = useState('');
    const [description, setDescription] = useState('');
    const [instructions, setInstructions] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [selectedAssessments, setSelectedAssessments] = useState('');
    const [selectedSubmissionAssessments, setSelectedSubmissionAssessments] = useState('');
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [minimumResponders, setMinimumResponders] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [allowExternalResponse, setAllowExternalResponse] = useState(true);
    const [targetGroup, setTargetGroup] = useState('all'); // 'all' or 'specific'
    const [assignmentType, setAssignmentType] = useState(2);

    // Form state for Department, Programme, Batch, Section
    const [formData, setFormData] = useState({
        department: { value: '', error: '', valid: false },
        programmeId: { value: '', error: '', valid: false },
        batch: { value: '', error: '', valid: false },
        section: { value: '', error: '', valid: false }
    });

    // Dropdown data
    const [assessmentData, setAssessmentData] = useState([]);
    const [submissionAssessmentData, setSubmissionAssessmentData] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [programList, setProgramList] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [studentData, setStudentData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [minendDate, setminendDate] = useState(new Date());
    const [categoryOptions] = useState([
        { id: 'psychological', value: 'Psychological' },
        { id: 'academic', value: 'Academic' },
        { id: 'research', value: 'Research' },
        { id: 'survey', value: 'Survey' },
        { id: 'feedback', value: 'Feedback' }
    ]);

    // Initial data load
    useEffect(() => {
        setIsLoading(true);
        Promise.all([ getDepartmentList(), programListData(), getAssessmentWithoutSummary()]).then(responses => {
            setDepartments(responses[0].map(elem => ({ id: elem.id, value: elem.title })));
            setProgramList(responses[1].map(elem => ({ id: elem.id, value: elem.programmename })));
            setSubmissionAssessmentData(responses[2].map(elem => ({ id: elem.id, value: elem.assessmentname })));

            // Set initial department if only one available
            let departmentId = '';
            if (responses[0].length === 1) {
                departmentId = responses[0][0].id;
            }

            // Generate year options for batch
            const startYear = 2000;
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let year = currentYear; year >= startYear; year--) {
                years.push({ value: year, option: year });
            }
            setYearOptions(years);

            // Set initial form values
            setFormData(prevState => ({
                ...prevState,
                department: { ...prevState.department, value: departmentId, valid: departmentId !== '' },
                batch: { ...prevState.batch, value: currentYear, valid: true }
            }));

            setSelectedCategory(categoryOptions[0].id);

            setIsLoading(false);
        }).catch(error => {
            console.error('Error loading initial data:', error);
            setIsLoading(false);
        });
    }, []);

    // Fetch students based on filters
    useEffect(() => {
        if (
            formData.department.valid &&
            formData.programmeId.valid &&
            formData.batch.valid &&
            formData.section.valid
        ) {
            fetchStudents();
        }
    }, [formData.department, formData.programmeId, formData.batch, formData.section]);

    useEffect(() => {
        if ( selectedCategory ) {
            getAssessmentList(selectedCategory).then(response => {
                setSelectedAssessments('');
                setAssessmentData(response.map(elem => ({ id: elem.id, value: elem.assessmentname })));
            }).catch(error => {
                console.log(error);
            });
        }
    }, [selectedCategory]);

    const getAssessmentList = (category) => {
        let payload = {
            field: ['id', 'assessmentname'],
            filter: [
                {
                    field: 'islatestversion',
                    value: 1,
                    op: '=',
                    join: 'AND'
                },
                {
                    field: "assessmentcategory",
                    value: category,
                    op: '=',
                    join: 'AND'
                }
            ]
        };
        return new Promise((resolve, reject) => {
            postApi(assessmentList, payload)
                .then(response => {
                    resolve(response.data.data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    const getDepartmentList = () => {
        let payload = {
            filter: []
        };

        // If logged-in user is role 4 (Department Head), only show their department
        if (loggedInUserData.roleid === 4) {
            payload.filter.push({
                field: 'id',
                value: loggedInUserData.departmentid,
                op: '=',
                join: 'AND'
            });
        }

        return new Promise((resolve, reject) => {
            postApi(departmentList, payload)
                .then(response => {
                    resolve(response.data.data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    const programListData = () => {
        let payload = {
            filter: []
        };
        return new Promise((resolve, reject) => {
            postApi(programmeList, payload)
                .then(response => {
                    resolve(response.data.data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    const fetchStudents = () => {
        let filter = [
            {
                field: 'id',
                value: loggedInUserData.id,
                op: '!=',
                join: 'AND'
            },
            {
                field: 'roleid',
                value: 5, // Getting only Assessee's
                op: '=',
                join: 'AND'
            }
        ];

        filter.push({
            field: 'departmentid',
            value: formData.department.value,
            op: '=',
            join: 'AND'
        });
        filter.push({
            field: 'programmeid',
            value: formData.programmeId.value,
            op: '=',
            join: 'AND'
        });
        filter.push({
            field: 'batch',
            value: formData.batch.value,
            op: '=',
            join: 'AND'
        });
        filter.push({
            field: 'section',
            value: `*${formData.section.value}*`,
            op: 'LIKE',
            join: 'AND'
        });

        let payload = {
            field: ['id', 'firstname', 'lastname', 'programmeid', 'section', 'batch', 'departmentid'],
            filter: filter
        };

        postApi(userList, payload)
            .then(response => {
                setStudentData(response?.data?.data?.map(elem => ({
                    id: elem.id,
                    value: elem.firstname + ' ' + elem.lastname
                })) || []);
                setSelectedStudents([]);
            })
            .catch(error => {
                console.log('Error fetching students:', error);
            });
    };

    const getAssessmentWithoutSummary = () => {
        let payload = {
            field: ['id', 'assessmentname'],
            filter: [
                {
                    field: 'islatestversion',
                    value: 1,
                    op: '=',
                    join: 'AND'
                },
                // {
                //     field: "scoreparameter",
                //     value: 2,
                //     op: '=',
                //     join: 'AND'
                // }
            ]
        };
        return new Promise((resolve, reject) => {
            postApi(assessmentList, payload)
                .then(response => {
                    resolve(response.data.data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    };

    const onFormDataChange = (field, value) => {
        setFormData(prevState => ({
            ...prevState,
            [field]: {
                ...prevState[field],
                value: value,
                valid: value !== '' && value !== null
            }
        }));
    };

    const onInputChange = (name, value) => {
        if ( name == 'assignmenttype' ) {
            setAssignmentType(value);
            if ( value == 2 ) {
                setSelectedAssessments('');
                setMinimumResponders('');
            }
        }
    };

    const saveRunAssessment = () => {
        let data = {
            runname: assignmentName,
            description: description,
            instruction: instructions,
            clientid: selectedStudents,
            assignmenttype: assignmentType,
            expirydate: moment(dueDate).utc().format("YYYY-MM-DD HH:mm:ss"),
            runtype: 3,
            departmentid: formData.department.value,
            programmeid: formData.programmeId.value,
            batch: formData.batch.value,
            section: formData.section.value,
            category: selectedCategory,
            allowexternalresponse: allowExternalResponse,
            targetgroup: targetGroup,
            rundate: moment.utc().format('YYYY-MM-DD HH:mm:ss')
        };

        if ( assignmentType == 1 ) {
            data.assessmentid = [selectedAssessments];
            data.submissionassessmentid = selectedSubmissionAssessments;
            data.respondersrequired = minimumResponders;
        } else if ( assignmentType == 2 ) {
            data.assessmentid = [selectedSubmissionAssessments];
        }

        postApi(runAdd, {data})
            .then(response => {
                if (response && response.status === 200) {
                    handleResetForm();
                    props.history.push('/assignments');
                }
            })
            .catch(error => {
                console.log('Error saving assignment:', error);
            });
    };

    const handleResetForm = () => {
        setAssignmentName('');
        setDescription('');
        setInstructions('');
        setDueDate('');
        setSelectedAssessments('');
        setSelectedSubmissionAssessments('');
        setSelectedStudents([]);
        setMinimumResponders('');
        setSelectedCategory('');
        setAllowExternalResponse(false);
        setTargetGroup('all');
        setFormData({
            department: { value: '', error: '', valid: false },
            programmeId: { value: '', error: '', valid: false },
            batch: { value: '', error: '', valid: false },
            section: { value: '', error: '', valid: false }
        });
    };

    const handleSelectAllStudents = () => {
        setSelectedStudents(studentData.map(s => s.id));
    };

    const handleClearAllStudents = () => {
        setSelectedStudents([]);
    };

    const filterPassedEndTime = (time) => {
        const currentDate = minendDate;
        const selectedDate = new Date(time);
        return currentDate.getTime() < selectedDate.getTime();
    };

    const isFormValid = () => {
        return (
            assignmentName.trim() !== '' &&
            assignmentType &&
            (assignmentType == 1 ? selectedAssessments !== '' && selectedSubmissionAssessments !== '' : selectedSubmissionAssessments !== '') &&
            (assignmentType == 1 ? minimumResponders !== '' && minimumResponders > 0 : true) &&
            selectedStudents.length > 0 &&
            dueDate !== '' &&
            formData.department.valid &&
            formData.programmeId.valid &&
            formData.batch.valid &&
            formData.section.valid
        );
    };

    return (
        <div className="create-assignment-container">
            <button
                type="button"
                className="btn-back  mb-3"
                onClick={() => props.history.push('/assignments')}
            >
                <Back />
            </button>

            <div className="create-assignment-header">
                <h1>Create New Assignment</h1>
                <p>Configure and assign assessments to students</p>
            </div>

            {isLoading ? (
                <div className="loader-container">
                    <img className='assessment-loader' src={`${process.env.PUBLIC_URL}/static/images/loader.svg`} alt="loader-icon" />
                </div>
            ) : (
                <>
                    {/* SET 1: Assignment Details */}
                    <div className="form-section">
                        <div className="section-header">
                            <h2>Assignment Details</h2>
                        </div>

                        <div className="form-group">
                            <label>
                                Title <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Assignment Title"
                                value={assignmentName}
                                onChange={(e) => setAssignmentName(e.target.value)}
                                className="form-input"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                placeholder="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="form-input textarea-input"
                                rows="4"
                            />
                        </div>

                        <div className="form-group">
                            <label>Instructions</label>
                            <textarea
                                placeholder="Instructions"
                                value={instructions}
                                onChange={(e) => setInstructions(e.target.value)}
                                className="form-input textarea-input"
                                rows="4"
                            />
                        </div>
                    </div>

                    {/* SET 2: Configuration */}
                    <div className="form-section d-flex flex-column gap-3">
                        <h4>Configuration</h4>

                        <div className="category-wrapper">
                            <div className="category-label-wrapper">
                                <label className="category-label">
                                    Category <span className="text-danger">*</span>
                                </label>
                            </div>
                            <div className="category-buttons">
                                {categoryOptions.map((cat) => (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(cat.id)}
                                    >
                                        {cat.value}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className=''>
                            <Form.Label>Assignment Type <span className="text-danger">*</span></Form.Label>
                            <Form className='d-flex run-type-controls gap-3'>
                                <Form.Check id="assignment-type-1" type="radio" label="Collect Samples, Analyse and Submit" name="assignmenttype" value={1} checked={assignmentType == 1} onChange={(e) => onInputChange(e.target.name, e.target.value)} />
                                <Form.Check id="assignment-type-2" type="radio" label="Submit" name="assignmenttype" value={2} checked={assignmentType == 2} onChange={(e) => onInputChange(e.target.name, e.target.value)} />
                            </Form>
                        </div>

                        {
                            assignmentType == 1 ? 
                            <div className='d-flex gap-2'>
                                <div className="flex-grow-1">
                                    <DropdownInput
                                        name={'assessment'}
                                        label="Tool / Instrument"
                                        isMandatory={true}
                                        value={selectedAssessments}
                                        defaultValue="Select Tool / Instrument"
                                        options={assessmentData}
                                        onChange={(data) => setSelectedAssessments(data)}
                                    />
                                    {assessmentData.length === 0 && (
                                        <span className="text-s text-info">No assessment(s) available</span>
                                    )}
                                </div>
                                <div className="flex-grow-1">
                                    <TextInput
                                        name="respondersrequired"
                                        label="Min Samples Required"
                                        placeholder="Min Samples Required"
                                        isMandatory={true}
                                        value={minimumResponders}
                                        onChange={(event) => setMinimumResponders(parseInt(event.target.value) || '')}
                                    />
                                </div>
                            </div> : ''
                        }

                        <div className="">
                            <DropdownInput
                                name={'assessment'}
                                value={selectedSubmissionAssessments}
                                label="Submission Questionnaire"
                                isMandatory={true}
                                defaultValue="Select Submission Questionnaire"
                                options={submissionAssessmentData}
                                onChange={(data) => setSelectedSubmissionAssessments(data)}
                            />
                            {submissionAssessmentData.length === 0 && (
                                <span className="text-s text-info">No assessment(s) available</span>
                            )}
                        </div> 

                        <div className="">
                            <div className="form-col">
                                <label>
                                    Due Date <span className="text-danger">*</span>
                                </label>
                                <div className="date-input-wrapper w-100">
                                    <DatePicker
                                        id="dueDate"
                                        name="dueDate"
                                        placeholderText="Due Date"
                                        icon="fa fa-calendar"
                                        showIcon
                                        showTimeSelect
                                        toggleCalendarOnIconClick
                                        dropdownMode="select"
                                        timeIntervals={15}
                                        dateFormat="MM/dd/yyyy hh:mm a"
                                        timeFormat="HH:mm:ss"
                                        selected={dueDate ? new Date(dueDate) : null}
                                        filterTime={filterPassedEndTime}
                                        minDate={new Date()}
                                        onChangeRaw={(event) => event.preventDefault()}
                                        onChange={(date) => setDueDate(date)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SET 3: Distribution & Target Group */}
                    <div className="form-section">
                        <div className="section-header">
                            <h2>Distribution</h2>
                        </div>

                        <div className="form-row">
                            <div className="form-col">
                                <Form.Label className="run-assesse-label">
                                    Department <span className="text-danger">*</span>
                                </Form.Label>
                                <DropdownInput
                                    size="md"
                                    name="Department"
                                    defaultValue="Select Department"
                                    value={formData.department.value}
                                    options={departments}
                                    onChange={(value) => onFormDataChange('department', value)}
                                    error={formData.department?.error}
                                />
                                {departments.length === 0 && (
                                    <span className="text-s text-info">No Department available</span>
                                )}
                            </div>

                            <div className="form-col">
                                <Form.Label className="run-assesse-label">
                                    Program <span className="text-danger">*</span>
                                </Form.Label>
                                <DropdownInput
                                    size="md"
                                    name="programmeId"
                                    defaultValue="Select Program"
                                    value={formData.programmeId.value}
                                    options={programList}
                                    onChange={(value) => onFormDataChange('programmeId', value)}
                                    error={formData.programmeId?.error}
                                />
                                {programList.length === 0 && (
                                    <span className="text-s text-info">No Programme available</span>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col">
                                <Form.Label className="run-assesse-label">
                                    Batch <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    as="select"
                                    id="batch"
                                    name="batch"
                                    value={formData.batch.value}
                                    error={formData.batch.error}
                                    onChange={(event) => onFormDataChange('batch', event.target.value)}
                                >
                                    <option disabled value="">
                                        Batch
                                    </option>
                                    {yearOptions?.map((year, key) => (
                                        <option key={key} value={year.value}>
                                            {year.value}
                                        </option>
                                    ))}
                                </Form.Control>
                            </div>

                            <div className="form-col">
                                <Form.Label className="run-assesse-label">
                                    Section <span className="text-danger">*</span>
                                </Form.Label>
                                <TextInput
                                    className="w-100 p-2"
                                    name="section"
                                    placeholder="Section"
                                    value={formData.section.value}
                                    onChange={(event) => onFormDataChange('section', event.target.value)}
                                    ignoreDefaultErrorSpace
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col">
                                <label className="form-group-label">Target Group <span className="text-danger">*</span></label>
                                <div className="target-group-options">
                                    <div className="radio-option">
                                        <input
                                            type="radio"
                                            id="allStudents"
                                            name="targetGroup"
                                            value="all"
                                            checked={targetGroup === 'all'}
                                            onChange={() => setTargetGroup('all')}
                                        />
                                        <label htmlFor="allStudents">All Students</label>
                                    </div>
                                    <div className="radio-option">
                                        <input
                                            type="radio"
                                            id="specificStudents"
                                            name="targetGroup"
                                            value="specific"
                                            checked={targetGroup === 'specific'}
                                            onChange={() => setTargetGroup('specific')}
                                        />
                                        <label htmlFor="specificStudents">Specific Students</label>
                                    </div>
                                    <div className="radio-option">
                                        <input
                                            type="radio"
                                            id="studentGroup"
                                            name="targetGroup"
                                            value="studentgroup"
                                            disabled
                                        />
                                        <label htmlFor="studentGroup" className="radio-option-disabled">Student Group</label>
                                    </div>
                                </div>
                            </div>

                            <div className="form-col">
                                <label className="form-group-label">
                                    Allow External Response
                                </label>
                                <div className='d-flex align-items-center gap-2'>
                                    <span className={`toggle-label ${!allowExternalResponse ? 'active' : ''}`}>No</span>
                                    <Form.Check
                                        className='allow-external-response'
                                        type="switch"
                                        id="allow-external-response"
                                        checked={allowExternalResponse}
                                        onChange={(e) => setAllowExternalResponse(e.target.checked)}
                                    />
                                    <span className={`toggle-label ${allowExternalResponse ? 'active' : ''}`}>Yes</span>
                                </div>
                            </div>
                        </div>

                        {targetGroup === 'specific' && (
                            <div className="form-group">
                                <label>Select Assessee <span className="text-danger">*</span></label>
                                <DropdownInputMultiple
                                    showSelectAll
                                    name="student"
                                    value={selectedStudents}
                                    placeholder="Select Assessee"
                                    data={studentData}
                                    onChange={(data) => setSelectedStudents(data)}
                                />
                                {studentData.length === 0 && (
                                    <span className="text-s text-info">No Assessee(s) available</span>
                                )}
                                <div className="students-count">
                                    {selectedStudents.length} student(s) selected
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="form-actions">
                        <button
                            type="button"
                            className={`btn-create ${!isFormValid() ? 'disabled' : ''}`}
                            onClick={saveRunAssessment}
                            disabled={!isFormValid()}
                        >
                            <span>+</span> Create Assignment
                        </button>
                        <button
                            type="button"
                            className="btn-reset"
                            onClick={handleResetForm}
                        >
                            Reset Form
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AssignmentAdd;
